import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  CardContent,
  IconButton,
  List,
  ListDivider,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieIcon from "@mui/icons-material/Movie";
import ArchiveIcon from "@mui/icons-material/Archive";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DateTime } from "./DateTime";
import FileUploadButton from "./FileUploadButton";
import { useTranslation } from "../i18n";
import {
  useFileAttachMutation,
  useFileListQuery,
  useUserMeQuery,
} from "../__generated__/types-and-hooks";
import { accessTokenManager } from "../models/AccessTokenManager";

export function getFileIconFromMime(mimeType: string) {
  if (!mimeType) return <InsertDriveFileIcon />;

  if (mimeType.startsWith("image/")) return <ImageIcon />;
  if (mimeType.startsWith("audio/")) return <AudiotrackIcon />;
  if (mimeType.startsWith("video/")) return <MovieIcon />;
  if (mimeType === "application/pdf") return <PictureAsPdfIcon />;
  if (
    mimeType === "application/zip" ||
    mimeType === "application/x-zip-compressed"
  )
    return <ArchiveIcon />;
  if (
    mimeType.startsWith("text/") ||
    mimeType.includes("word") ||
    mimeType.includes("officedocument")
  ) {
    return <DescriptionIcon />;
  }

  return <InsertDriveFileIcon />;
}

function formatBytes(bytes: number) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024,
    sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export interface FilePermissionProps {
  permission: {
    access: string;
    role: string;
    subject: string;
    subjectId: number;
  };
}

export function FilePermission({ permission }: FilePermissionProps) {
  return (
    <Chip
      variant="outlined"
      color="neutral"
      size="lg"
      startDecorator={
        permission.subject === "user" ? <PersonIcon /> : <GroupIcon />
      }
      onClick={() => alert("You clicked the Joy Chip!")}
    >
      {permission.access}:{permission.subjectId}:{permission.role}
    </Chip>
  );
}

export interface FilesCardProps {
  entityId: string;
  entityType: string;
}

export function FilesCard({ entityId, entityType }: FilesCardProps) {
  const me = useUserMeQuery();
  const [fileAttachMutation] = useFileAttachMutation();
  const t = useTranslation();
  const [filesProcessing, setFilesProcessing] = useState<{
    files: { name: string; size: number; type: string }[];
    progress: number;
  } | null>(null);
  const [showCheckmark, setShowCheckmark] = useState(false);

  useEffect(() => {
    if (showCheckmark) {
      const timer = setTimeout(() => {
        setShowCheckmark(false); // or trigger the next step
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showCheckmark]);
  const fileListQuery = useFileListQuery({
    variables: {
      entityId,
      entityType,
    },
  });

  const handleDownload = async (file: {
    uniqueId: string;
    name?: string | null;
  }) => {
    try {
      const response = await fetch(
        process.env.REACT_APP_FILES_URL! + "/" + file.uniqueId,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessTokenManager.retrieve()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = file.name ?? "file"; // or derive from response headers
      document.body.appendChild(a);
      a.click();

      // Cleanup
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
        padding: 0,
        mb: 2,
        breakInside: "avoid",
      }}
      variant="outlined"
    >
      <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
        {t("Files")}
        <Box
          sx={{
            ml: 2,
            position: "absolute",
            top: "4px",
            display: "inline-block",
          }}
        >
          <FileUploadButton
            multiple
            onProgress={(files, progress) => {
              setFilesProcessing({ files, progress });
            }}
            onSuccess={async (files) => {
              setShowCheckmark(true);
              for (const file of files) {
                await fileAttachMutation({
                  variables: {
                    fileUniqueId: file.uniqueId,
                    entityId,
                    entityType,
                  },
                });
              }
              await fileListQuery.refetch();
              setFilesProcessing(null);
              if (navigator.vibrate) {
                navigator.vibrate(100); // Vibrate for 100ms
              }
            }}
          />
        </Box>
      </Typography>

      <CardContent
        sx={{
          m: 0,
          margin: "2px",
          borderRadius: 5,
          border: "1px solid black",
          bgcolor: "background.surface",
          gap: 0,
          p: 1,
          pl: 2,
        }}
      >
        <Sheet sx={{ overflow: "auto" }}>
          <List
            aria-labelledby="ellipsis-list-demo"
            sx={{ "--ListItemDecorator-size": "56px" }}
          >
            {(filesProcessing?.files ?? []).map((file, index) => (
              <>
                <ListItem key={index}>
                  <ListItemContent>
                    <Typography
                      sx={{ wordBreak: "break-all" }}
                      startDecorator={
                        <IconButton disabled>
                          {showCheckmark ||
                          filesProcessing?.progress === 100 ? (
                            <CheckCircleIcon
                              sx={{ color: "green", fontSize: 16 }}
                            />
                          ) : (
                            <CircularProgress
                              value={filesProcessing?.progress}
                              sx={{
                                "--CircularProgress-size": "16px",
                                "--CircularProgress-trackThickness": "2px",
                                "--CircularProgress-progressThickness": "1px",
                              }}
                            />
                          )}
                        </IconButton>
                      }
                      level="title-sm"
                    >
                      {file.name}
                    </Typography>
                    <Stack direction="row" gap={1}>
                      <Typography
                        level="body-sm"
                        textColor="neutral.500"
                        pl={5}
                        noWrap
                      >
                        {formatBytes(file.size!)}
                      </Typography>
                      <Typography
                        level="body-sm"
                        textColor="neutral.500"
                        noWrap
                      >
                        <DateTime value={new Date()} />
                      </Typography>
                    </Stack>
                  </ListItemContent>
                  <ListItemDecorator>
                    <Stack alignItems="end">
                      <Avatar size="sm" src={me.data?.user_me?.picture ?? ""} />
                      <Typography
                        textAlign="right"
                        level="body-sm"
                        textColor="neutral.500"
                      >
                        {me.data?.user_me?.firstName}{" "}
                        {me.data?.user_me?.lastName}
                      </Typography>
                    </Stack>
                  </ListItemDecorator>
                </ListItem>
                <ListDivider inset="gutter" />
              </>
            ))}
            {fileListQuery.data?.file_list?.files.length === 0 && (
              <Typography>{t("No files")}</Typography>
            )}
            {fileListQuery.data?.file_list?.files.map((file) => (
              <>
                <ListItem key={file.uniqueId}>
                  <ListItemContent>
                    <Typography
                      sx={{ wordBreak: "break-all" }}
                      startDecorator={
                        <IconButton onClick={() => handleDownload(file)}>
                          <DownloadIcon />
                        </IconButton>
                      }
                      level="title-sm"
                    >
                      {file.name}
                    </Typography>
                    <Stack direction="row" gap={1}>
                      <Typography
                        level="body-sm"
                        textColor="neutral.500"
                        pl={5}
                        noWrap
                      >
                        {formatBytes(file.size!)}
                      </Typography>
                      <Typography
                        level="body-sm"
                        textColor="neutral.500"
                        noWrap
                      >
                        <DateTime value={file.createdAt} />
                      </Typography>
                    </Stack>
                  </ListItemContent>
                  <ListItemDecorator>
                    <Stack alignItems="end">
                      <Avatar
                        size="sm"
                        src={file.createdByUser?.picture ?? ""}
                      />
                      <Typography
                        textAlign="right"
                        level="body-sm"
                        textColor="neutral.500"
                      >
                        {file.createdByUser?.firstName}{" "}
                        {file.createdByUser?.lastName}
                      </Typography>
                    </Stack>
                  </ListItemDecorator>
                </ListItem>
                <ListDivider inset="gutter" />
              </>
            ))}
          </List>
        </Sheet>
      </CardContent>
    </Card>
  );
}
